/**
 * Country Detection Loader
 *
 * Loading elegante mientras se detecta el país del usuario.
 * Diseño premium con animaciones fluidas; colores alineados al brand del sitio (env `VITE_BRAND_*`).
 *
 * UX optimizada:
 * - Animación de 500-800ms (tiempo de detección API)
 * - Skeleton loader con gradientes
 * - Transición suave a checkout
 */

import { Globe, Loader2, MapPin } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface CountryDetectionLoaderProps {
  /** Mensaje personalizado (opcional) */
  message?: string;
  /** Mostrar país detectado cuando esté disponible */
  detectedCountry?: string | null;
  /** Confianza de la detección */
  confidence?: "high" | "medium" | "low" | "unknown";
  /** Ciudad detectada (opcional, desde Cloudflare Worker) */
  city?: string | null;
}

export function CountryDetectionLoader({
  message,
  detectedCountry,
  city,
}: CountryDetectionLoaderProps) {
  const { t, i18n } = useTranslation();
  const displayMessage = message ?? t("countryLoader.defaultMessage");
  const [dots, setDots] = useState("");
  const [progress, setProgress] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Animación de dots
  useEffect(() => {
    if (reduceMotion) return;
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, [reduceMotion]);

  // Animación de progreso (simula detección)
  useEffect(() => {
    // Progreso rápido inicial
    const timer1 = setTimeout(() => setProgress(30), 100);
    const timer2 = setTimeout(() => setProgress(60), 300);

    // Si ya tenemos país, completar rápido
    if (detectedCountry) {
      const timer3 = setTimeout(() => setProgress(100), 400);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }

    // Si no, progreso más lento
    const timer3 = setTimeout(() => setProgress(80), 600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [detectedCountry]);

  const progressLabel = useMemo(() => {
    if (progress < 40) return t("countryLoader.progressDetect");
    if (progress < 70) return t("countryLoader.progressVerify");
    if (progress < 100) return t("countryLoader.progressPrep");
    return t("countryLoader.progressRedirect");
  }, [progress, t]);

  // Antes: el gradient cambiaba según `confidence` (purple→pink→blue→green/amber)
  // y arrancaba en purple "unknown" antes de mutar al final de la detección.
  // Eso producía un flicker cromático cada vez que entraba un nuevo signal
  // del worker (country → city → AMBA refinement). Ahora un único tono neutro
  // mantiene la pantalla quieta visualmente — el progreso lo comunica la barra,
  // no el color.
  const ACCENT_GRADIENT = "from-white/30 to-white/10";

  return (
    <div
      className="relative w-full h-dvh flex flex-col items-center justify-center text-white overflow-hidden"
      role="status"
      aria-live="polite"
      aria-busy={progress < 100}
      style={{
        // BG unificado con BrandSplashScreen (App.tsx) + TemplateLoadingScreen
        // + index.html #initial-splash: SIEMPRE #0a0a0a base + accent gradient
        // sutil encima. Sin esto, cuando este loader aparece tras el splash de
        // boot, había cambio drástico a purple — flash visible al usuario.
        background: `
          radial-gradient(ellipse 120% 70% at 50% -10%, rgba(139, 92, 246, 0.10) 0%, transparent 55%),
          radial-gradient(ellipse 90% 60% at 100% 100%, rgba(20, 20, 50, 0.85) 0%, transparent 52%),
          linear-gradient(165deg, #0a0a0a 0%, #141428 45%, #1a1030 100%)
        `,
      }}
    >
      {/* Animated background — blobs unificadas en un solo tono neutro para que
          el visitor no perciba "muchos colores" durante la detección. El base
          radial-gradient ya carga el matiz purple sutil del brand splash; las
          blobs sólo aportan movimiento, no más croma. */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <div
          className={`absolute inset-0 bg-white/[0.03] ${
            reduceMotion ? "" : "animate-pulse"
          }`}
        />
        <div
          className={`absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl ${
            reduceMotion ? "opacity-40" : "animate-blob"
          }`}
        />
        <div
          className={`absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl ${
            reduceMotion ? "opacity-40" : "animate-blob animation-delay-2000"
          }`}
        />
        <div
          className={`absolute top-1/2 left-1/2 w-96 h-96 bg-white/10 rounded-full blur-3xl ${
            reduceMotion ? "opacity-40" : "animate-blob animation-delay-4000"
          }`}
        />
      </div>

      {/* Content */}
      <div
        className={`relative z-10 flex flex-col items-center gap-6 px-6 max-w-md mx-auto ${
          reduceMotion ? "" : "animate-fade-in"
        }`}
      >
        {/* Icon with pulsing effect */}
        <div className="relative">
          {!reduceMotion && (
            <div
              className={`absolute inset-0 bg-gradient-to-r ${ACCENT_GRADIENT} rounded-full blur-2xl opacity-50 animate-pulse`}
              aria-hidden
            />
          )}
          <div
            className={`relative w-20 h-20 bg-gradient-to-br ${ACCENT_GRADIENT} rounded-full flex items-center justify-center shadow-2xl`}
          >
            {/* Icono fijo: el swap Globe→MapPin cuando llegaba el país era otra
                discontinuidad visual encima del cambio de color. Globe siempre
                girando da continuidad y deja que el cambio lo cuente el texto
                (país/ciudad debajo). */}
            <Globe
              className={`w-10 h-10 text-white ${reduceMotion ? "" : "animate-spin-slow"}`}
              aria-hidden
            />
          </div>
        </div>

        {/* Main message */}
        <div className="text-center space-y-2">
          <h2 id="country-loader-title" className="text-2xl font-bold text-white">
            {displayMessage}
            <span className="inline-block w-8 text-left" aria-hidden>
              {dots}
            </span>
          </h2>

          {detectedCountry && (
            <div
              className={`flex flex-col items-center justify-center gap-1 ${
                reduceMotion ? "" : "animate-fade-in"
              }`}
            >
              <div className="flex items-center gap-2 text-sm text-white/70">
                <MapPin className="w-4 h-4 shrink-0" aria-hidden />
                <span>{getCountryDisplay(detectedCountry, i18n.language)}</span>
              </div>
              {city && <div className="text-xs text-white/50">{city}</div>}
            </div>
          )}

          {/* Confidence label removido: cambiaba el texto a mitad de la
              detección (low → medium → high) y reforzaba la sensación de que
              "el loading se mueve". El usuario ya sabe que está cargando por
              la barra de progreso. */}
        </div>

        {/* Loading bar with progress */}
        <div className="w-full max-w-xs space-y-2">
          <div
            className="h-1.5 bg-white/10 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            aria-label={t("countryLoader.progressAria", { pct: Math.round(progress) })}
          >
            <div
              className={`h-full bg-gradient-to-r ${ACCENT_GRADIENT} rounded-full ${
                reduceMotion ? "" : "transition-all duration-300 ease-out"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {progress > 0 && (
            <p
              className={`text-center text-white/40 text-xs ${
                reduceMotion ? "" : "animate-fade-in"
              }`}
              aria-live="polite"
            >
              {progressLabel}
            </p>
          )}
        </div>

        {/* Subtle footer */}
        <p className="text-white/40 text-xs text-center">
          {t("countryLoader.footer")}
        </p>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-blob,
          .animate-pulse,
          .animate-bounce,
          .animate-fade-in,
          .animate-spin-slow {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Mini loader inline (para usar dentro de botones o modales)
 */
export function CountryDetectionMiniLoader() {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-2 text-white/70"
      role="status"
      aria-live="polite"
      aria-label={t("countryLoader.mini")}
    >
      <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden />
      <span className="text-sm">{t("countryLoader.mini")}</span>
    </div>
  );
}

/**
 * Mapeo de códigos ISO2 a etiqueta con bandera; si no hay entrada, usa Intl.DisplayNames.
 */
function getCountryDisplay(countryCode: string, locale: string): string {
  const upper = countryCode.toUpperCase();
  const countryMap: Record<string, string> = {
    // LATAM
    AR: "🇦🇷 Argentina",
    MX: "🇲🇽 México",
    CL: "🇨🇱 Chile",
    CO: "🇨🇴 Colombia",
    BR: "🇧🇷 Brasil",
    PE: "🇵🇪 Perú",
    UY: "🇺🇾 Uruguay",
    PY: "🇵🇾 Paraguay",
    BO: "🇧🇴 Bolivia",
    EC: "🇪🇨 Ecuador",
    VE: "🇻🇪 Venezuela",
    CR: "🇨🇷 Costa Rica",
    PA: "🇵🇦 Panamá",
    GT: "🇬🇹 Guatemala",
    DO: "🇩🇴 República Dominicana",
    HN: "🇭🇳 Honduras",
    SV: "🇸🇻 El Salvador",
    NI: "🇳🇮 Nicaragua",
    CU: "🇨🇺 Cuba",
    PR: "🇵🇷 Puerto Rico",

    // Europa
    ES: "🇪🇸 España",
    DE: "🇩🇪 Alemania",
    FR: "🇫🇷 Francia",
    IT: "🇮🇹 Italia",
    PT: "🇵🇹 Portugal",
    GB: "🇬🇧 Reino Unido",
    NL: "🇳🇱 Países Bajos",
    BE: "🇧🇪 Bélgica",
    CH: "🇨🇭 Suiza",
    AT: "🇦🇹 Austria",
    IE: "🇮🇪 Irlanda",
    SE: "🇸🇪 Suecia",
    NO: "🇳🇴 Noruega",
    DK: "🇩🇰 Dinamarca",
    FI: "🇫🇮 Finlandia",
    PL: "🇵🇱 Polonia",
    CZ: "🇨🇿 Chequia",
    HR: "🇭🇷 Croacia",
    GR: "🇬🇷 Grecia",
    SK: "🇸🇰 Eslovaquia",
    SI: "🇸🇮 Eslovenia",
    LU: "🇱🇺 Luxemburgo",
    MT: "🇲🇹 Malta",
    CY: "🇨🇾 Chipre",
    EE: "🇪🇪 Estonia",
    LV: "🇱🇻 Letonia",
    LT: "🇱🇹 Lituania",
    IS: "🇮🇸 Islandia",
    RO: "🇷🇴 Rumanía",
    HU: "🇭🇺 Hungría",
    BG: "🇧🇬 Bulgaria",

    // Norteamérica
    US: "🇺🇸 Estados Unidos",
    CA: "🇨🇦 Canadá",
  };

  if (countryMap[upper]) return countryMap[upper];

  try {
    const lang = locale.split("-")[0] || "en";
    const name = new Intl.DisplayNames([locale, lang, "en"], {
      type: "region",
    }).of(upper);
    if (name) return name;
  } catch {
    /* ignore */
  }

  return upper;
}
